import { useTranslation } from 'react-i18next';
import { PARTNER_ACCOUNT_STATUSES, Select, withStyles } from 'vopay';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import {
  isOrganizationShowFilter,
  isOrganizationStatusFilter,
  ORGANIZATION_SHOW_FILTERS,
  OrganizationShowFilter,
  OrganizationStatusFilter,
} from './organizationTree';
import styles from './OrganizationChartControls.module.scss';

const cn = withStyles(styles);

/** The Status filter's "every status" value; the URL carries no status for it. */
const ALL_STATUSES = 'all';

type OrganizationOverviewFiltersProps = {
  show: OrganizationShowFilter;
  onShowChange: (show: OrganizationShowFilter) => void;
  /** False for a partner whose book holds no partner branches to show or hide: the Show filter is left out. */
  hasShowFilter: boolean;
  /** The status filtered to, '' for every status. */
  status: OrganizationStatusFilter;
  onStatusChange: (status: OrganizationStatusFilter) => void;
};

/** The Overview's Show and Status filters (spec 6.3), on the right of the chart's control row. */
export default function OrganizationOverviewFilters({
  show,
  onShowChange,
  hasShowFilter,
  status,
  onStatusChange,
}: OrganizationOverviewFiltersProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Entities.OrganizationChartControls' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  const showOptions = ORGANIZATION_SHOW_FILTERS.map((filter) => ({
    value: filter,
    label: t(`Show.${filter}`, { defaultValue: filter }),
  }));

  // The raw status stays the option value (it is what the filter matches); only the label is localized.
  const statusOptions = [
    { value: ALL_STATUSES, label: t('allStatuses', { defaultValue: 'All' }) },
    ...PARTNER_ACCOUNT_STATUSES.map((accountStatus) => ({
      value: accountStatus,
      label: resolveStatusLabel(accountStatus),
    })),
  ];

  return (
    <>
      {hasShowFilter && (
        <>
          <span className={cn('label')}>{t('showLabel', { defaultValue: 'Show' })}</span>

          <div className={cn('field')}>
            <Select
              version="2"
              name="show"
              value={show}
              options={showOptions}
              sort={false}
              onChange={(_, { value }) => {
                if (isOrganizationShowFilter(value)) onShowChange(value);
              }}
            />
          </div>
        </>
      )}

      <div className={cn('field')}>
        <Select
          version="2"
          name="status"
          placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
          // No status reads as the placeholder (Figma); "All" is the option that clears one.
          value={status}
          options={statusOptions}
          sort={false}
          onChange={(_, { value }) => onStatusChange(isOrganizationStatusFilter(value) ? value : '')}
        />
      </div>
    </>
  );
}
